import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../services/firebase";
import { FormLabel } from "../../components/form-label/FormLabel";
import { Conversation } from "../../types";

const STATUS_COLOR: Record<string, "success" | "warning" | "error" | "default"> = {
  active: "warning",
  completed: "success",
  failed: "error",
};

export default function ConversationDetail() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const [searchParams] = useSearchParams();
  const tenantIdParam = searchParams.get("tenantId") ?? "";
  const flowIdParam = searchParams.get("flowId") ?? "";
  const navigate = useNavigate();

  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!conversationId) return;
    const docRef = doc(db, "conversations", decodeURIComponent(conversationId));
    const unsub = onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          setConversation(snap.data() as Conversation);
        } else {
          setError("Conversazione non trovata");
        }
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setError("Errore nel caricamento della conversazione");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [conversationId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversation?.messages?.length]);

  const backUrl = tenantIdParam
    ? `/chatbot/monitoring/conversations?tenantId=${tenantIdParam}${flowIdParam ? `&flowId=${flowIdParam}` : ""}`
    : "/chatbot/monitoring/conversations";

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="container">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <FormLabel label={`Conversazione: ${conversation?.user_phone ?? "…"}`} />
        <Button variant="outlined" onClick={() => navigate(backUrl)}>
          Indietro
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {conversation && (
        <>
          {/* Flow state summary */}
          {conversation.flow_state && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Stato flow
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                <Chip
                  label={conversation.flow_state.status}
                  color={STATUS_COLOR[conversation.flow_state.status] ?? "default"}
                  size="small"
                />
                <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                  Flow: {conversation.flow_state.flow_id.slice(0, 12)}…
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                  Step: {conversation.flow_state.current_step_id.slice(0, 12)}…
                </Typography>
              </Box>

              {/* Why it stopped (failed flows) */}
              {conversation.flow_state.last_error && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  Interrotto ({conversation.flow_state.last_error.code})
                  {conversation.flow_state.last_error.step_id
                    ? ` allo step ${conversation.flow_state.last_error.step_id.slice(0, 12)}…`
                    : ""}
                  : {conversation.flow_state.last_error.message}
                </Alert>
              )}

              {/* Provenance: which EGG event/rule triggered this execution */}
              {conversation.flow_state.trigger && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Innescato da evento {conversation.flow_state.trigger.source.toUpperCase()} —
                    evento {conversation.flow_state.trigger.event_id ?? conversation.flow_state.trigger.internal_event_id}
                    , regola {conversation.flow_state.trigger.rule_id.slice(0, 12)}…
                  </Typography>
                </Box>
              )}

              {/* Executed steps trail */}
              {conversation.flow_state.step_history &&
                conversation.flow_state.step_history.length > 0 && (
                  <Box sx={{ mb: 1, display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                      Percorso:
                    </Typography>
                    {conversation.flow_state.step_history.map((s, i) => (
                      <Chip
                        key={`${s.step_id}-${i}`}
                        label={s.type}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}

              {Object.keys(conversation.flow_state.collected_data).length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Dati raccolti finora
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
                    <Table size="small">
                      <TableBody>
                        {Object.entries(conversation.flow_state.collected_data).map(
                          ([key, value]) => (
                            <TableRow key={key}>
                              <TableCell sx={{ fontFamily: "monospace", fontWeight: 600, width: 180 }}>
                                {key}
                              </TableCell>
                              <TableCell>{String(value)}</TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Paper>
          )}

          {/* Messages */}
          <Paper
            variant="outlined"
            sx={{ p: 2, maxHeight: 500, overflowY: "auto" }}
            className="flex-col gap-2"
          >
            <Typography variant="subtitle2" gutterBottom>
              Messaggi ({conversation.messages?.length ?? 0})
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {(conversation.messages ?? []).map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent: msg.role === "user" ? "flex-start" : "flex-end",
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    maxWidth: "75%",
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor:
                      msg.role === "user" ? "#f0f0f0" : "#e3f2fd",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.25 }}
                  >
                    {msg.role}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {msg.content}
                  </Typography>
                </Box>
              </Box>
            ))}
            <div ref={bottomRef} />
          </Paper>
        </>
      )}
    </div>
  );
}
